/**
 * 护理时间轴面板
 * 重构为"每日护理看板"设计
 * 去商业化，专注于清晰度和用户依从性
 */

import React, { useMemo, useCallback } from "react";
// import { type TFunction } from "i18next";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import type { AnalysisResult, RiskLevel } from "@/types/care-plan";

// Care Plan Components
import { DailyCareBoard } from "@/components/care-plan/DailyCareBoard";
import { DiagnosisHeader } from "@/components/care-plan/DiagnosisHeader";
import { EmergencyTipsCard } from "@/components/care-plan/EmergencyTipsCard";

// Hooks & Types
import { useCarePlan } from "@/hooks/useCarePlan";
import { AssessmentRecord, ParsedAssessment } from "@/types/assessments";
import { ToolboxItem } from "@/components/assessments/ToolboxSelector";
import type { DailyScheduleDay, DailyTask } from "@/types/care-plan";
import { isFissionSchedule } from "@/types/care-plan";

export interface CarePlanTimelinePanelProps {
  assessmentId: string;
  currentAssessment: AssessmentRecord | null | undefined;
  parsedAssessment: ParsedAssessment | null;
  selectedToolboxItems: ToolboxItem[];
  onBack: () => void;
}

/**
 * 从 parsedAssessment 的 care_steps 构建后备时间表
 * 当 daily_routine 为空时使用
 */
function buildFallbackSchedule(
  parsedAssessment: any,
  t: any,
  riskLevel?: RiskLevel,
): DailyScheduleDay[] {
  const isHighRisk = riskLevel === "high" || riskLevel === "medium";
  const careSteps = parsedAssessment?.stage_3_private?.care_steps || [];

  // 定义阶段到天数的映射
  const phaseToDay: Record<string, number> = {
    紧急处理: 0,
    急救: 0,
    "Day 0": 0,
    隔离: 0,
    升温: 0,
    下盐: 0,
    观察: 1,
    "Day 1": 1,
    "Day 2": 2,
    换水: 3,
    "Day 3": 3,
    "Day 4": 4,
    恢复: 5,
    "Day 5": 5,
    "Day 6": 6,
    "Day 7": 7,
  };

  // 按天分组任务
  const dayMap = new Map<number, DailyTask[]>();

  // 如果有 care_steps，按 phase 分组
  if (careSteps.length > 0) {
    // 智能分配策略：
    // - 前 3 个任务 -> Day 0（急救）
    // - 剩余任务按索引均匀分配到 Day 1-7
    const emergencyCount = Math.min(3, careSteps.length);
    const remainingSteps = careSteps.length - emergencyCount;

    careSteps.forEach((step: any, index: number) => {
      let dayIndex: number;

      if (index < emergencyCount) {
        // 前 3 个任务放到 Day 0
        dayIndex = 0;
      } else {
        // 剩余任务均匀分配到 Day 1-7
        const remainingIndex = index - emergencyCount;
        dayIndex = 1 + Math.floor((remainingIndex / remainingSteps) * 7);
        dayIndex = Math.min(dayIndex, 7); // 确保不超过 Day 7
      }

      if (!dayMap.has(dayIndex)) {
        dayMap.set(dayIndex, []);
      }

      dayMap.get(dayIndex)!.push({
        id: `sop-${dayIndex}-${index}`,
        original_step_id: `step-${index}`,
        day_index: dayIndex,
        action: step.action,
        product_key: step.toolbox_item_used || null,
        action_type:
          dayIndex === 0 ? (isHighRisk ? "critical" : "routine") : "routine",
        is_continued: false,
        is_emergency: dayIndex === 0 && isHighRisk,
        badge: step.phase || null,
        priority_score: 100 - index * 10,
        status: "pending",
      });
    });
  } else {
    // 默认护理任务（当没有 care_steps 时）
    const defaultTasks: Array<{ day: number; action: string; type: string }> = [
      {
        day: 0,
        action: t("tasks.check_temp", "检查水温是否在适宜范围"),
        type: "critical",
      },
      {
        day: 0,
        action: t("tasks.observe_symptoms", "观察鱼的异常行为和体表症状"),
        type: "critical",
      },
      {
        day: 0,
        action: t("tasks.prepare_isolation", "准备隔离容器（如需要）"),
        type: "critical",
      },
      {
        day: 1,
        action: t("tasks.continue_observation", "继续观察症状变化"),
        type: "observation",
      },
      {
        day: 1,
        action: t("tasks.check_water_quality", "检查水质指标"),
        type: "routine",
      },
      {
        day: 2,
        action: t("tasks.check_improvement", "观察症状是否好转"),
        type: "observation",
      },
      {
        day: 3,
        action: t("tasks.change_water_minor", "少量换水 (10-20%)"),
        type: "routine",
      },
      {
        day: 4,
        action: t("tasks.observe_recovery", "观察恢复情况"),
        type: "observation",
      },
      {
        day: 5,
        action: t("tasks.evaluate_treatment", "评估是否需要继续护理"),
        type: "routine",
      },
      {
        day: 6,
        action: t("tasks.resume_feeding", "逐步恢复正常喂食"),
        type: "routine",
      },
      {
        day: 7,
        action: t("tasks.summarize_effect", "总结护理效果"),
        type: "routine",
      },
    ];

    defaultTasks.forEach((task, index) => {
      if (!dayMap.has(task.day)) {
        dayMap.set(task.day, []);
      }

      dayMap.get(task.day)!.push({
        id: `default-${task.day}-${index}`,
        original_step_id: `default-${index}`,
        day_index: task.day,
        action: task.action,
        product_key: null,
        action_type: task.type as "critical" | "observation" | "routine",
        is_continued: false,
        is_emergency: task.day === 0 && isHighRisk,
        badge: null,
        priority_score: 100 - index * 5,
        status: "pending",
      });
    });
  }

  // 构建 8 天的时间表
  const schedule: DailyScheduleDay[] = [];
  const dayConfigs = [
    {
      label: isHighRisk
        ? t("phases.emergency_period", "关键应对期")
        : t("phases.observation_period", "观察与药浴"),
      title: isHighRisk
        ? t("phases.emergency", "紧急应对")
        : t("phases.observation", "观察"),
      title_key: isHighRisk
        ? "phases.emergency_period"
        : "phases.observation_period",
    },
    {
      label: t("phases.observation_period", "观察与药浴"),
      title: t("phases.observation", "观察"),
      title_key: "phases.observation_period",
    },
    {
      label: t("phases.observation_period", "观察与药浴"),
      title: t("phases.observation", "观察"),
      title_key: "phases.observation_period",
    },
    {
      label: t("phases.recovery_period", "恢复期"),
      title: t("phases.recovery", "恢复"),
      title_key: "phases.recovery_period",
    },
    {
      label: t("phases.recovery_period", "恢复期"),
      title: t("phases.recovery", "恢复"),
      title_key: "phases.recovery_period",
    },
    {
      label: t("phases.recovery_period", "恢复期"),
      title: t("phases.recovery", "恢复"),
      title_key: "phases.recovery_period",
    },
    {
      label: t("phases.consolidation_period", "巩固期"),
      title: t("phases.consolidation", "巩固"),
      title_key: "phases.consolidation_period",
    },
    {
      label: t("phases.consolidation_period", "巩固期"),
      title: t("phases.consolidation", "巩固"),
      title_key: "phases.consolidation_period",
    },
  ];

  for (let i = 0; i < 8; i++) {
    const tasks = dayMap.get(i) || [];
    const config = dayConfigs[i] || { label: "恢复期", title: "恢复" };

    schedule.push({
      day_index: i,
      day: i,
      title: config.title,
      title_key: config.title_key,
      day_label: config.label,
      is_emergency: i === 0 && isHighRisk,
      is_current: i === 0,
      date_display: `Day ${i}`,
      tasks,
      tasks_count: tasks.length,
      is_observation_period: tasks.length === 0,
    });
  }

  return schedule;
}

export const CarePlanTimelinePanel: React.FC<CarePlanTimelinePanelProps> = ({
  assessmentId,
  currentAssessment,
  parsedAssessment,
  selectedToolboxItems,
  onBack,
}) => {
  const { t } = useTranslation("assessments");

  // 使用 useCarePlan hook 获取护理时间轴数据
  const {
    timeline: carePlanTimeline,
    isLoading: isCarePlanLoading,
    isUnlocked: isCarePlanUnlocked,
    currentDay,
    completeTask,
    undoCompleteTask,
    completedTaskIds,
  } = useCarePlan({
    assessmentId: assessmentId,
    enabled: !!assessmentId,
  });

  // 将 daily_routine 转换为 DailyScheduleDay[] 格式
  const schedule = useMemo((): DailyScheduleDay[] => {
    // 优先使用 carePlanTimeline 中的数据
    if (carePlanTimeline?.daily_routine?.length) {
      const routine = carePlanTimeline.daily_routine;

      // 如果已经是 Fission 结构，直接使用
      if (isFissionSchedule(routine)) {
        // 检查是否有实际任务
        const hasTasks = routine.some((day) => day.tasks.length > 0);
        if (hasTasks) {
          return routine;
        }
      } else {
        // 兼容旧版 DailyRoutineDay 结构
        const hasTasks = routine.some((day) => day.tasks.length > 0);
        if (hasTasks) {
          return routine.map((day, index) => ({
            day_index: index,
            day: day.day,
            title: day.is_observation_period ? "观察" : "护理",
            day_label: day.day_label,
            is_emergency: day.day === 0,
            is_current: day.day === currentDay,
            date_display: `Day ${day.day}`,
            tasks: day.tasks.map((task) => ({
              id: task.id,
              original_step_id: task.id,
              day_index: index,
              action: task.action,
              product_key: null,
              action_type: day.is_observation_period
                ? "observation"
                : "critical",
              is_continued: false,
              is_emergency: day.day === 0,
              badge: task.action_detail || null,
              priority_score: 100 - index * 10,
              status: task.completed ? "completed" : "pending",
            })),
            tasks_count: day.tasks.length,
            is_observation_period: day.is_observation_period || false,
          }));
        }
      }
    }

    // 后备：从 parsedAssessment 构建（包含默认任务）
    const riskLevel =
      carePlanTimeline?.analysis_result?.risk_level ||
      (currentAssessment?.stage_2_public?.severity_level as RiskLevel) ||
      (parsedAssessment?.severity as RiskLevel) ||
      (parsedAssessment?.stage_2_public?.severity_level as RiskLevel);
    return buildFallbackSchedule(parsedAssessment, t, riskLevel);
  }, [
    carePlanTimeline?.daily_routine,
    carePlanTimeline?.analysis_result,
    currentAssessment,
    currentDay,
    parsedAssessment,
    t,
  ]);

  // 使用 ref 追踪正在处理中的任务，防止重复点击
  const processingTasksRef = React.useRef<Set<string>>(new Set());

  // 任务切换处理（乐观更新 + 防重复点击）
  const handleTaskToggle = useCallback(
    (taskId: string, completed: boolean) => {
      // 如果该任务正在处理中，直接忽略
      if (processingTasksRef.current.has(taskId)) {
        console.log("Task is already being processed:", taskId);
        return;
      }

      // 标记任务为处理中
      processingTasksRef.current.add(taskId);

      // 设置超时清除处理状态（防止异常情况下卡死）
      setTimeout(() => {
        processingTasksRef.current.delete(taskId);
      }, 2000);

      if (completed) {
        completeTask(taskId, currentDay);
      } else {
        undoCompleteTask(taskId);
      }

      // 正常情况下，150ms 后移除处理状态
      setTimeout(() => {
        processingTasksRef.current.delete(taskId);
      }, 150);
    },
    [completeTask, undoCompleteTask, currentDay],
  );

  return (
    <motion.div
      key="timeline-full-panel"
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 100 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="w-full max-w-4xl mx-auto px-4 sm:px-6 pt-4 pb-8"
    >
      <div className="space-y-6">
        {/* 顶部：分析结论（极简元数据） */}
        {carePlanTimeline?.analysis_result && (
          <DiagnosisHeader
            result={carePlanTimeline.analysis_result}
            currentDay={currentDay}
            totalDays={schedule.length > 0 ? schedule.length : 7}
          />
        )}

        {/* 核心：每日护理看板（统一控制台） */}
        {isCarePlanLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : schedule.length > 0 ? (
          <DailyCareBoard
            schedule={schedule}
            currentDay={currentDay}
            completedTaskIds={completedTaskIds}
            onTaskToggle={handleTaskToggle}
            isLoading={isCarePlanLoading}
          />
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            {t("carePlan.loadError", "无法加载护理方案")}
          </div>
        )}
      </div>
    </motion.div>
  );
};
